import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { FetchMoreQueryOptions, FetchMoreOptions } from "@apollo/client";
import { StoresV2Query } from "../../../__generated__/graphql";

interface UseInfiniteScrollOptions {
  limit?: number;
  lat: number | null;
  lng: number | null;
  hasLastLo: boolean;
  getLocationFromStorage: () => { lat: number; lng: number };
  fetchMore: ((
    fetchMoreOptions: FetchMoreQueryOptions<any, any> & FetchMoreOptions<any, any>
  ) => Promise<any>) | null;
  isList: boolean;
  storageKey?: string;
  scrollId?: string;
}

export const useInfiniteScroll = ({
  limit = 20,
  lat,
  lng,
  hasLastLo,
  getLocationFromStorage,
  fetchMore,
  isList,
  storageKey = "home",
  scrollId = "scroll-home",
}: UseInfiniteScrollOptions) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const fetchMoreRef = useRef(fetchMore);
  const [isScrollRestored, setIsScrollRestored] = useState(false);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);

  // fetchMore가 변경될 때마다 ref 업데이트
  useEffect(() => {
    fetchMoreRef.current = fetchMore;
  }, [fetchMore]);

  // 현재 위치 정보 (위치가 변경되면 저장된 데이터 무효화용)
  const currentLocationKey = useMemo(() => {
    const currentLat = lat
      ? Number(lat)
      : hasLastLo
      ? getLocationFromStorage().lat
      : null;
    const currentLng = lng
      ? Number(lng)
      : hasLastLo
      ? getLocationFromStorage().lng
      : null;
    return `${currentLat}-${currentLng}`;
  }, [lat, lng, hasLastLo, getLocationFromStorage]);

  // sessionStorage에서 복원
  const restoreFromStorage = useCallback(() => {
    try {
      const savedStores = sessionStorage.getItem(`${storageKey}:allStores`);
      const savedOffset = sessionStorage.getItem(`${storageKey}:offset`);
      const savedHasMore = sessionStorage.getItem(`${storageKey}:hasMore`);
      const savedLocationKey = sessionStorage.getItem(`${storageKey}:locationKey`);

      // 위치가 변경되었으면 저장된 데이터 무효화
      if (savedLocationKey !== currentLocationKey) {
        sessionStorage.removeItem(`${storageKey}:allStores`);
        sessionStorage.removeItem(`${storageKey}:offset`);
        sessionStorage.removeItem(`${storageKey}:hasMore`);
        sessionStorage.removeItem(`${storageKey}:locationKey`);
        return null;
      }

      if (savedStores && savedOffset && savedHasMore) {
        const parsedStores = JSON.parse(savedStores);
        return {
          stores: parsedStores,
          offset: parseInt(savedOffset, 10),
          hasMore: savedHasMore === "true",
        };
      }
    } catch (error) {
      console.error("Failed to restore from storage:", error);
    }
    return null;
  }, [currentLocationKey, storageKey]);

  const restoredData = restoreFromStorage();
  const [offset, setOffset] = useState(restoredData?.offset || 0);
  const [allStores, setAllStores] = useState<StoresV2Query["storesV2"]>(
    restoredData?.stores || []
  );
  const [hasMore, setHasMore] = useState(restoredData?.hasMore ?? true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initLoading, setInitLoading] = useState(!restoredData);

  // allStores 변경 시 sessionStorage에 저장
  useEffect(() => {
    if (allStores && allStores.length > 0) {
      try {
        sessionStorage.setItem(`${storageKey}:allStores`, JSON.stringify(allStores));
        sessionStorage.setItem(`${storageKey}:offset`, offset.toString());
        sessionStorage.setItem(`${storageKey}:hasMore`, hasMore.toString());
        sessionStorage.setItem(`${storageKey}:locationKey`, currentLocationKey);
      } catch (error) {
        console.error("Failed to save to storage:", error);
      }
    }
  }, [allStores, offset, hasMore, currentLocationKey, storageKey]);

  // 복원된 데이터가 있으면 즉시 로딩 완료 처리
  useEffect(() => {
    if (restoredData && restoredData.stores.length > 0) {
      setInitLoading(false);
    } else if (!restoredData && (!allStores || allStores.length === 0)) {
      // 복원된 데이터가 없고 현재 데이터도 없으면 초기화 상태로 복귀
      setInitLoading(true);
    }
  }, [restoredData, allStores]);

  // 위치가 변경되면 저장된 데이터 무효화 및 상태 초기화
  useEffect(() => {
    const savedLocationKey = sessionStorage.getItem(`${storageKey}:locationKey`);
    if (savedLocationKey && savedLocationKey !== currentLocationKey) {
      // 위치가 변경되었으므로 상태 초기화
      setOffset(0);
      setAllStores([]);
      setHasMore(true);
      setInitLoading(true);
      setHasUserScrolled(false);
      setIsScrollRestored(false);
    }
  }, [currentLocationKey, storageKey]);

  // 스크롤 복원 완료 감지 및 사용자 스크롤 감지
  useEffect(() => {
    const scrollContainer = rootRef.current;
    if (!scrollContainer) return;

    // 스크롤 복원이 완료되었는지 확인 (ScrollRestore가 스크롤 위치를 설정한 후)
    const checkScrollRestored = () => {
      const savedScroll = sessionStorage.getItem(scrollId);
      if (savedScroll && parseInt(savedScroll, 10) > 0) {
        // 복원된 스크롤 위치가 있으면 복원 완료로 간주
        setTimeout(() => {
          setIsScrollRestored(true);
        }, 100);
      } else {
        // 저장된 스크롤이 없으면 즉시 복원 완료로 간주
        setIsScrollRestored(true);
      }
    };

    // 초기 체크
    checkScrollRestored();

    // 사용자 스크롤 감지
    let lastScrollTop = scrollContainer.scrollTop;
    const handleScroll = () => {
      const currentScrollTop = scrollContainer.scrollTop;
      // 스크롤 위치가 변경되었고, 프로그래밍 방식이 아닌 사용자 스크롤인 경우
      if (Math.abs(currentScrollTop - lastScrollTop) > 5) {
        setHasUserScrolled(true);
      }
      lastScrollTop = currentScrollTop;
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [allStores, scrollId]);

  // 더 많은 매장 불러오기 함수
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !fetchMoreRef.current) return;

    setIsLoadingMore(true);

    try {
      const result = await fetchMoreRef.current({
        variables: {
          lat: lat
            ? Number(lat)
            : hasLastLo
            ? getLocationFromStorage().lat
            : null,
          lng: lng
            ? Number(lng)
            : hasLastLo
            ? getLocationFromStorage().lng
            : null,
          offset: offset,
          limit: limit,
        },
      });

      if (result.data?.storesV2) {
        const newStores = result.data.storesV2;

        // 중복 제거하여 추가
        setAllStores((prev) => {
          if (!prev) return newStores;
          const existingIds = new Set(
            prev
              .map((store) => (store as any)?.id)
              .filter((id): id is number => id != null)
          );
          const uniqueNewStores = newStores.filter(
            (store: any) => store && store.id && !existingIds.has(store.id)
          );
          return [...prev, ...uniqueNewStores];
        });

        setOffset((prev) => prev + newStores.length);
        setHasMore(newStores.length === limit);
      }
    } catch (error) {
      console.error("Failed to load more stores:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore,
    hasMore,
    lat,
    lng,
    hasLastLo,
    getLocationFromStorage,
    offset,
    limit,
  ]);

  // Intersection Observer로 무한 스크롤 구현
  useEffect(() => {
    if (
      !hasMore ||
      isLoadingMore ||
      !isList ||
      !allStores ||
      allStores.length === 0
    )
      return;

    // 스크롤이 복원되었고 사용자가 실제로 스크롤했을 때만 observer 활성화
    // 또는 복원된 데이터가 없을 때 (처음 방문한 경우)
    const shouldActivateObserver =
      !restoredData || (isScrollRestored && hasUserScrolled);

    if (!shouldActivateObserver) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: rootRef.current,
        rootMargin: "300px",
      }
    );

    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [
    hasMore,
    isLoadingMore,
    handleLoadMore,
    isList,
    allStores,
    restoredData,
    isScrollRestored,
    hasUserScrolled,
  ]);

  // 상태 초기화 함수
  const reset = useCallback(() => {
    setOffset(0);
    setAllStores([]);
    setHasMore(true);
    setInitLoading(true);
    setHasUserScrolled(false);
    setIsScrollRestored(false);
    // sessionStorage 초기화
    sessionStorage.removeItem(`${storageKey}:allStores`);
    sessionStorage.removeItem(`${storageKey}:offset`);
    sessionStorage.removeItem(`${storageKey}:hasMore`);
    sessionStorage.removeItem(`${storageKey}:locationKey`);
  }, [storageKey]);

  return {
    rootRef,
    loadMoreRef,
    allStores,
    setAllStores,
    offset,
    setOffset,
    hasMore,
    setHasMore,
    isLoadingMore,
    initLoading,
    setInitLoading,
    isScrollRestored,
    hasUserScrolled,
    reset,
  };
};

